import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import Confetti from 'react-confetti';
import RichTextRenderer from '../common/RichTextRenderer';
import './StudentDashboard.css';
import {
  FaBookOpen,
  FaCheckCircle,
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaCalendar,
  FaMapMarkerAlt,
  FaClock,
  FaEye,
  FaSave,
  FaClipboardList,
  FaFileAlt,
  FaInfoCircle,
  FaStepForward,
  FaDumbbell
} from 'react-icons/fa';
import { FiExternalLink, FiCheck } from 'react-icons/fi';
import {
  startCourse,
  nextContent,
  getProgress,
  saveProgress,
  getNavigationContext,
  bootstrapStudent,
  fetchCourseContent
} from '../../actions/authActions';
import { getEvents } from '../../actions/eventActions';
import { getUserSubmissionStatus } from '../../actions/submissionActions';
import { getCourseContent, calculateCourseProgress, isContentCompleted, getContentTypeLabel, getContentTypeIcon, getAllPathContent, getCourseForContent, isContentNavigable, validateGlobalNavigation } from '../../utils/progressUtils';
import { getStoredCompletion, setStoredCompletion } from './utils/modalHelpers';
import { getUserXP } from '../../actions/xpActions';
import EventsSection from './EventsSection';
import CourseCarouselSection from './CourseCarouselSection';
import CourseProgressSection from './CourseProgressSection';
import LoadingStates from './LoadingStates';
import ImageUpload from '../common/ImageUpload';
import SubmissionFormContent from './SubmissionFormContent';
import PathCompletionModal from './PathCompletionModal';
import CourseDetailModal from './CourseDetailModal';
import ContentModalHeader from './ContentModalHeader';
import ModalNavigationButtons from './ModalNavigationButtons';
import LessonContentView from './LessonContentView';
import ReinforcementContentView from './ReinforcementContentView';
import ReinforcementPopup from './ReinforcementPopup';

// Import main dashboard hook
import { useStudentDashboard } from './hooks/useStudentDashboard';


const StudentDashboard = ({
  auth,
  event: { events, loading: eventsLoading },
  xp: { userXP, loading: xpLoading },
  getEvents,
  startCourse,
  nextContent,
  getProgress,
  saveProgress,
  getNavigationContext,
  bootstrapStudent,
  fetchCourseContent,
  getUserSubmissionStatus,
  getUserXP
}) => {
  // Use main dashboard hook for all state management
  const {
    // Core state
    user,
    courseDetails,
    userCurrentCourse,
    userCurrentContent,
    currentContentType,
    navigationContext,
    pathCourses,
    userProgress,
    lastAction,
    userSubmissionStatus,

    // Progress state
    pathProgress,
    currentStep,
    courseProgress,
    totalSteps,
    displayedCourse,
    displayedCourseName,

    // Modal state
    showLessonModal,
    selectedCourse,
    setSelectedCourse,
    hasAutoOpenedModal,
    setHasAutoOpenedModal,
    hasShownCompletionModal,
    modalNavigation,
    setModalNavigation,
    currentContentIndex,
    modalCourseContent,

    // Achievement state
    showAchievementView,
    setShowAchievementView,
    hasUserClosedAchievement,
    hasShownAutoCompletion,
    setHasShownAutoCompletion,
    showAchievementConfetti,
    setShowAchievementConfetti,
    hasShownAchievementConfetti,
    setHasShownAchievementConfetti,
    windowDimension,
    expandedLessons,
    setExpandedLessons,
    courseActionLoading,
    setCourseActionLoading,
    nextContentLoading,
    setNextContentLoading,

    // Submission state
    submissionData,
    setSubmissionData,
    urlError,
    setUrlError,
    submissionStatusLoading,
    isSubmitting,
    isWaitingForStatus,
    isSubmitDisabled,
    collaborators,
    setCollaborators,

    // Helper functions
    getCompletedCoursesLocal,
    isCourseCompletedLocal,
    getCourseProgressLocal,
    getCompletedContentForCourseLocal,
    isPathCompletedLocal,
    setShowContentModalWithDebug,
    resetToActualProgressLocal,
    getDisplayedContentLocal,
    getDisplayedContentTypeLocal,
    getDisplayedContentIndexLocal,
    handleBackNavigationLocal,
    handleForwardBrowsingLocal,
    handleCloseLessonModal,
    validateUrlLocal,
    handleUrlChange,
    getCurrentSubmissionStatus,
    canProgressFromSubmission,
    handleSubmitSubmission,
    toggleLessonAccordion,
    handleAchievementClose,

    // Redux actions (only the ones that exist)
    addSubmission,
    updateSubmission
    // Note: Other completion actions are not available in current codebase
  } = useStudentDashboard();

  // Course content lazy-load state
  const [loadingCourseId, setLoadingCourseId] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Reinforcement popup state management
  const [showReinforcementPopup, setShowReinforcementPopup] = useState(false);
  const [pendingReinforcementContent, setPendingReinforcementContent] = useState(null);

  // Track which reinforcements have already been shown in this session
  const shownReinforcementsRef = useRef(new Set());

  // Auto-show reinforcement popup when modal opens with reinforcement as current content
  useEffect(() => {
    // When modal is open, current content is a reinforcement, and we haven't shown the popup yet
    if (showLessonModal && currentContentType === 'reinforcement' && userCurrentContent && !showReinforcementPopup && !pendingReinforcementContent) {
      const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;

      // Only show popup if we haven't already shown this specific reinforcement
      if (!shownReinforcementsRef.current.has(contentId)) {
        shownReinforcementsRef.current.add(contentId);
        setPendingReinforcementContent(userCurrentContent);
        setShowReinforcementPopup(true);
      }
    }
  }, [showLessonModal, currentContentType, userCurrentContent, showReinforcementPopup, pendingReinforcementContent]);

  // Helper function to calculate content index excluding reinforcements
  const getContentIndexWithoutReinforcements = (course, contentId) => {
    if (!course || !contentId) return 0;

    const courseContent = getCourseContent(course);
    let countWithoutReinforcements = 0;

    for (const item of courseContent) {
      // Check if this item is a reinforcement
      const isReinforcement = course.reinforcements?.some(r => r._id === item._id);

      // If we found our target content, return the count
      if (item._id === contentId) {
        return countWithoutReinforcements;
      }

      // Only increment count for non-reinforcement items
      if (!isReinforcement) {
        countWithoutReinforcements++;
      }
    }

    return countWithoutReinforcements;
  };


  // Calculate current content index without reinforcements
  // When viewing backwards, use the viewing content; otherwise use actual progress
  const currentContentIndexWithoutReinforcements = (() => {
    // When viewing backwards, use the viewing content and course
    if (modalNavigation.isViewingBackwards && modalNavigation.viewingContent && modalNavigation.viewingCourse) {
      return getContentIndexWithoutReinforcements(
        modalNavigation.viewingCourse,
        modalNavigation.viewingContent._id
      );
    }

    // Otherwise, use actual progress position
    if (userCurrentCourse && userCurrentContent) {
      return getContentIndexWithoutReinforcements(
        userCurrentCourse,
        typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent
      );
    }

    return 0;
  })();

  // modalCourseContent is already filtered to exclude reinforcements in useModalNavigation hook
  const totalContentWithoutReinforcements = modalCourseContent.length;

  // Derived state
  const studentPath = user?.path;
  const isLoading = false; // TODO: Handle loading states properly
  const progressLoading = false;

  // Track bootstrap to only run once per user session
  const bootstrapRef = useRef(null);

  // Bootstrap: load full path/courses/progress data for student (heavy, one-time)
  useEffect(() => {
    const userId = user?.id || user?._id;
    if (user?.role === 'student' && userId && bootstrapRef.current !== userId) {
      bootstrapRef.current = userId;
      bootstrapStudent();
      getUserXP();
      getNavigationContext();
      // program may be an ObjectId (lightweight auth) or populated object (bootstrap)
      const program = user.group?.program;
      const programId = typeof program === 'object' && program !== null ? program._id : program;
      if (programId) {
        getEvents(programId);
      }
    }
  }, [user, bootstrapStudent, getUserXP, getNavigationContext, getEvents]);

  // Old useEffect hooks removed - now handled by custom hooks

  // Clear next content loading when both Redux progress states are complete
  useEffect(() => {
    if (!auth.progressLoading && !auth.isContentProgressing && nextContentLoading) {
      // Immediately clear local loading state once Redux confirms completion
      setNextContentLoading(false);
    }
  }, [auth.progressLoading, auth.isContentProgressing, nextContentLoading]);



  // Determine start button text
  const getStartButtonText = () => {
    if (!userCurrentCourse) return "Start Course";
    if (userCurrentContent) return "JUMP BACK IN";
    return "Start Course";
  };

  // Determine next button text
  const getNextButtonText = () => {
    if (!userCurrentContent) {
      return "Start First Content";
    }

    // Check if this is the last content in the current course
    const isLastContentInCourse = currentContentIndex === modalCourseContent.length - 1;

    if (isLastContentInCourse) {
      // Check if this is the last course in the path
      const currentCourseIndex = pathCourses.findIndex(course => course._id === userCurrentCourse?._id);
      const isLastCourseInPath = currentCourseIndex === pathCourses.length - 1;

      if (isLastCourseInPath) {
        return "Complete Path";
      } else {
        return "Next Course";
      }
    } else {
      return "Next Content";
    }
  };

  // All helper functions are now provided by the hooks

  // Modal auto-open is now handled by useModalNavigation hook

  // Achievement modal tracking is now handled by useAchievementState hook

  // ✅ BUGFIX EFFECTS REMOVED - No longer needed!
  // The backend now always provides valid currentContent and currentContentType,
  // so the modal closing bug is fundamentally solved.

  // Achievement view and confetti are now handled by useAchievementState hook

  // Body scroll management is now handled by useModalNavigation hook




  // Show loading state while essential data is being fetched
  if (!user || isLoading) {
    return <LoadingStates.DashboardLoading />;
  }

  // Handle course start - always open content modal
  const handleStartCourse = () => {
    const buttonText = getStartButtonText();

    // Reset navigation state to actual progress when opening modal
    resetToActualProgressLocal();

    if (buttonText === "JUMP BACK IN") {
      // User has a current lesson, just open modal
      setShowContentModalWithDebug(true);
    } else {
      // "Start Course" - call API first, then modal will auto-open on success
      setCourseActionLoading(true);
      setHasAutoOpenedModal(false); // Reset flag so modal can auto-open after API call
      startCourse();
      // Note: Loading will be cleared by Redux state change or timeout
      setTimeout(() => setCourseActionLoading(false), 5000); // Fallback timeout
    }
  };

  // Handle content progression with peek-ahead for reinforcements
  const handleNextContent = () => {
    const nextButtonText = getNextButtonText();

    // Peek-ahead: Check if next content is reinforcement
    let nextContentItem = null;
    let nextContentTypeValue = null;

    // IMPORTANT: Always check current course first for reinforcements, regardless of button text
    // This ensures we catch reinforcements at the end of courses
    const currentCourseContent = getCourseContent(userCurrentCourse);
    const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;
    const actualCurrentIndex = currentCourseContent.findIndex(c => c._id === contentId);
    const nextContentIndex = actualCurrentIndex + 1;

    // First, check if there's more content in the CURRENT course (including reinforcements)
    if (nextContentIndex < currentCourseContent.length) {
      nextContentItem = currentCourseContent[nextContentIndex];

      // Determine content type by checking which array it belongs to
      const isLesson = userCurrentCourse.lessons?.some(l => l._id === nextContentItem._id);
      const isSubmissionForm = userCurrentCourse.submissionForms?.some(s => s._id === nextContentItem._id);
      const isReinforcement = userCurrentCourse.reinforcements?.some(r => r._id === nextContentItem._id);

      if (isReinforcement) {
        nextContentTypeValue = 'reinforcement';
      } else if (isSubmissionForm) {
        nextContentTypeValue = 'submissionForm';
      } else if (isLesson) {
        nextContentTypeValue = 'lesson';
      }

    } else if (nextButtonText === "Next Course") {
      // Only check next course if we've exhausted current course content
      const currentCourseIndex = pathCourses.findIndex(c => c._id === userCurrentCourse?._id);
      const nextCourseIndex = currentCourseIndex + 1;

      if (nextCourseIndex < pathCourses.length) {
        const nextCourse = pathCourses[nextCourseIndex];
        const nextCourseContent = getCourseContent(nextCourse);

        if (nextCourseContent.length > 0) {
          nextContentItem = nextCourseContent[0];

          // Determine content type
          const isLesson = nextCourse.lessons?.some(l => l._id === nextContentItem._id);
          const isSubmissionForm = nextCourse.submissionForms?.some(s => s._id === nextContentItem._id);
          const isReinforcement = nextCourse.reinforcements?.some(r => r._id === nextContentItem._id);

          if (isReinforcement) {
            nextContentTypeValue = 'reinforcement';
          } else if (isSubmissionForm) {
            nextContentTypeValue = 'submissionForm';
          } else if (isLesson) {
            nextContentTypeValue = 'lesson';
          }

          setHasAutoOpenedModal(false); // Allow auto-open for new course
        }
      }
    } else if (nextButtonText === "Complete Path") {
      // Achievement view will be handled by useEffect when backend confirms completion
    }

    // If next content is reinforcement, show popup WITHOUT calling API
    if (nextContentTypeValue === 'reinforcement') {
      // Show popup directly with the reinforcement
      setPendingReinforcementContent(nextContentItem);
      setShowReinforcementPopup(true);

      // Don't call API, don't change current content
      return;
    }

    // Not a reinforcement - proceed normally with API call
    setNextContentLoading(true);

    // Reset navigation state before making progress update
    resetToActualProgressLocal();

    // Make the API call - backend will provide updated currentContent and currentContentType
    nextContent();
    // Note: Loading will be cleared by Redux state change or fallback timeout
    setTimeout(() => setNextContentLoading(false), 3000); // Fallback timeout (reduced from 5s to 3s)
  };

  // handleCloseLessonModal is now provided by the hook

  const handleSaveAndQuit = () => {
    // Prevent the auto-open useEffect from interfering during save operation
    setHasAutoOpenedModal(true);

    // Reset navigation state to actual progress
    resetToActualProgressLocal();

    // Close the modal immediately
    setShowContentModalWithDebug(false);
    setShowAchievementView(false);

    // Then perform the async operations
    saveProgress();
    getProgress();
  };

  // Handle reinforcement popup close with auto-advance
  const handleReinforcementPopupClose = () => {

    // Close the popup UI
    setShowReinforcementPopup(false);
    setPendingReinforcementContent(null);

    // Set loading state
    setNextContentLoading(true);
    resetToActualProgressLocal();

    // Single API call with skipReinforcement flag
    // Backend will automatically skip the reinforcement and return the next content
    nextContent(true);

    // Clear loading state after transition
    setTimeout(() => setNextContentLoading(false), 3000);
  };

  // toggleLessonAccordion is now provided by useAchievementState hook

  // Validate URL function
  const validateUrl = (url) => {
    if (!url || url.trim() === '') {
      setUrlError('');
      return true;
    }
    
    try {
      new URL(url.trim());
      setUrlError('');
      return true;
    } catch (error) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
      return false;
    }
  };

  // handleUrlChange is now provided by useSubmissionState hook

  // Handle submission form display
  // Handle submission form submission
  // handleSubmitSubmission is now provided by the hook

  // Show message if no path assigned
  if (!studentPath) {
    return <LoadingStates.NoPathAssigned />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto">

        {/* Top bar + Hero progress */}
        <CourseProgressSection
          user={user}
          studentPath={studentPath}
          pathCourses={pathCourses}
          userProgress={userProgress}
          pathProgress={pathProgress}
          currentStep={currentStep}
          totalSteps={totalSteps}
          userXP={userXP}
          onStartCourse={handleStartCourse}
          progressLoading={progressLoading || courseActionLoading}
        />

        <div className="border-t border-border" />

        {/* Course Carousel */}
        <CourseCarouselSection
          pathCourses={pathCourses}
          userCurrentCourse={userCurrentCourse}
          userProgress={userProgress}
          isCourseCompleted={isCourseCompletedLocal}
          getCourseProgress={getCourseProgressLocal}
          getCourseContent={getCourseContent}
          navigationContext={navigationContext}
          modalNavigation={modalNavigation}
          onCourseClick={async (course) => {
            setExpandedLessons(new Set());
            const courseId = course._id;
            // Use cached full course if available, otherwise fetch
            const full = courseDetails[courseId] || await (async () => {
              setLoadingCourseId(courseId);
              try { return await fetchCourseContent(courseId); }
              finally { setLoadingCourseId(null); }
            })();
            setSelectedCourse(full);
          }}
          loadingCourseId={loadingCourseId}
          onStartCourse={handleStartCourse}
          progressLoading={progressLoading || courseActionLoading}
          coursesLoading={pathCourses.length === 0}
          getStartButtonText={getStartButtonText}
        />

        <div className="border-t border-border" />

        {/* Events */}
        <EventsSection
          events={events}
          eventsLoading={eventsLoading}
          onEventClick={(event) => setSelectedCourse(event)}
        />

        {/* Content Modal */}
        {(() => {
          const shouldShowModal = showLessonModal && (userCurrentContent || modalCourseContent.length > 0 || isPathCompletedLocal() || showAchievementView);
          return shouldShowModal;
        })() && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div className="bg-white w-full h-full flex flex-col relative">
              {/* Modal Header */}
              <ContentModalHeader
                showAchievementView={showAchievementView}
                isPathCompletedLocal={isPathCompletedLocal}
                modalNavigation={modalNavigation}
                userCurrentCourse={userCurrentCourse}
                currentContentType={currentContentType}
                studentPath={studentPath}
                userProgress={userProgress}
                pathCourses={pathCourses}
                getCourseProgressLocal={getCourseProgressLocal}
                getCompletedContentForCourseLocal={getCompletedContentForCourseLocal}
                courseProgress={courseProgress}
                modalCourseContent={modalCourseContent}
                onClose={handleCloseLessonModal}
              />

              {/* Lesson Content - Full height, scrollable with extra bottom padding for FABs */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-8 pb-24 relative">
                {(showAchievementView || isPathCompletedLocal()) ? (
                  /* Path Completion Congratulations Screen */
                  <PathCompletionModal
                    studentPath={studentPath}
                    pathCoursesLength={pathCourses.length}
                    showConfetti={showAchievementConfetti}
                    windowDimension={windowDimension}
                    onClose={handleCloseLessonModal}
                  />
                ) : currentContentType === 'reinforcement' ? (
                  /* Blank background when reinforcement is current content - popup will overlay */
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-sm">Reinforcement content loading...</p>
                  </div>
                ) : getDisplayedContentLocal() ? (
                  <div>
                    {/* Course Context Breadcrumb for Cross-Course Navigation */}

                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-5 gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">
                          Item {currentContentIndexWithoutReinforcements + 1} of {totalContentWithoutReinforcements}
                          {modalNavigation.isViewingBackwards && (
                            <span className="ml-2 text-orange-500">· Reviewing</span>
                          )}
                        </p>
                        <h4 className="text-xl sm:text-2xl font-bold text-foreground">
                          {getDisplayedContentLocal().title}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2 self-start flex-shrink-0">
                        {getDisplayedContentTypeLocal() === 'lesson' && getDisplayedContentLocal().slug && (
                          <button
                            onClick={() => {
                              const url = `https://qa.yourbigyear.cloud/lesson/${getDisplayedContentLocal().slug}`;
                              navigator.clipboard.writeText(url);
                              setShareCopied(true);
                              setTimeout(() => setShareCopied(false), 2000);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"
                            title="Copy lesson link"
                          >
                            {shareCopied ? (
                              <><FiCheck className="w-3 h-3 text-green-600" /> Copied!</>
                            ) : (
                              <><FiExternalLink className="w-3 h-3" /> Share Link</>
                            )}
                          </button>
                        )}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          getDisplayedContentTypeLocal() === 'submissionForm'
                            ? 'bg-mission text-mission-foreground'
                            : 'bg-lesson text-lesson-foreground'
                        }`}>
                          {getDisplayedContentTypeLocal() === 'submissionForm' ? 'Mission' : 'Lesson'}
                        </span>
                      </div>
                    </div>

                    {getDisplayedContentTypeLocal() === 'submissionForm' ? (
                      <SubmissionFormContent
                        userCurrentContent={getDisplayedContentLocal()}
                        submissionStatus={getCurrentSubmissionStatus()}
                        submissionData={submissionData}
                        setSubmissionData={setSubmissionData}
                        urlError={urlError}
                        submissionStatusLoading={submissionStatusLoading}
                        isSubmitting={isSubmitting}
                        isWaitingForStatus={isWaitingForStatus}
                        isViewingBackwards={modalNavigation.isViewingBackwards}
                        handleUrlChange={handleUrlChange}
                        getCurrentSubmissionStatus={getCurrentSubmissionStatus}
                        handleSubmitSubmission={handleSubmitSubmission}
                        userSubmissionStatus={userSubmissionStatus}
                        collaborators={collaborators}
                        setCollaborators={setCollaborators}
                        tokenConfig={auth.token ? { headers: { Authorization: `Bearer ${auth.token}` } } : {}}
                      />
                    ) : (
                      <LessonContentView
                        isViewingBackwards={modalNavigation.isViewingBackwards}
                        getDisplayedContentLocal={getDisplayedContentLocal}
                      />
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <LoadingStates.LoadingWithMessage message="Please wait while we load the course content." />
                  </div>
                )}
              </div>

              {/* Fixed Floating Action Buttons - positioned outside scrollable content */}
              <ModalNavigationButtons
                showAchievementView={showAchievementView}
                isPathCompletedLocal={isPathCompletedLocal}
                getDisplayedContentLocal={getDisplayedContentLocal}
                modalNavigation={modalNavigation}
                navigationContext={navigationContext}
                handleBackNavigationLocal={handleBackNavigationLocal}
                handleSaveAndQuit={handleSaveAndQuit}
                getCurrentSubmissionStatus={getCurrentSubmissionStatus}
                getDisplayedContentTypeLocal={getDisplayedContentTypeLocal}
                handleSubmitSubmission={handleSubmitSubmission}
                userCurrentContent={userCurrentContent}
                submissionData={submissionData}
                urlError={urlError}
                handleForwardBrowsingLocal={handleForwardBrowsingLocal}
                handleNextContent={handleNextContent}
                nextContentLoading={nextContentLoading}
                progressLoading={auth.progressLoading}
                isContentProgressing={auth.isContentProgressing}
                canProgressFromSubmission={canProgressFromSubmission}
                currentContentIndex={currentContentIndexWithoutReinforcements}
                getNextButtonText={getNextButtonText}
                modalCourseContentLength={totalContentWithoutReinforcements}
                userSubmissionStatus={userSubmissionStatus}
              />
            </div>
          </div>,
          document.body
        )}

        {/* Course Detail Modal */}
        <CourseDetailModal
          selectedCourse={selectedCourse}
          onClose={() => {
            setExpandedLessons(new Set());
            setSelectedCourse(null);
          }}
          userProgress={userProgress}
          userCurrentContent={userCurrentContent}
          userCurrentCourse={userCurrentCourse}
          courseProgress={courseProgress}
          isCourseCompletedLocal={isCourseCompletedLocal}
          expandedLessons={expandedLessons}
          setExpandedLessons={setExpandedLessons}
          toggleLessonAccordion={toggleLessonAccordion}
          onLessonClick={(content) => {
            // Close detail modal
            setExpandedLessons(new Set());
            setSelectedCourse(null);

            // Find the global index of this content
            const allPathContent = navigationContext?.allPathContent || [];
            const globalIndex = allPathContent.findIndex(c => c._id === content._id);
            const course = getCourseForContent(content._id, pathCourses);

            if (globalIndex === -1 || !course) return;

            // Find local index within the course
            const courseContent = getCourseContent(course);
            const localIndex = courseContent.findIndex(c => c._id === content._id);

            // Set modal navigation to view this content
            setModalNavigation({
              viewingContent: content,
              viewingContentType: content.type,
              viewingIndex: localIndex,
              globalIndex: globalIndex,
              viewingCourse: course,
              isViewingBackwards: true,
              allPathContent: allPathContent,
              canNavigateBack: globalIndex > 0,
              canNavigateForward: true
            });

            // Open the content modal
            setShowContentModalWithDebug(true);
          }}
        />

        {/* Reinforcement Popup */}
        <ReinforcementPopup
          isOpen={showReinforcementPopup}
          content={pendingReinforcementContent}
          onClose={handleReinforcementPopupClose}
        />

      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  event: state.event,
  submission: state.submission,
  xp: state.xp
});

export default connect(
  mapStateToProps,
  {
    getEvents,
    startCourse,
    nextContent,
    getProgress,
    saveProgress,
    getNavigationContext,
    bootstrapStudent,
    fetchCourseContent,
    getUserSubmissionStatus,
    getUserXP
  }
)(StudentDashboard);
